import {
  ContributionType,
  DebtContribution,
  ExcessAmountMethod,
  excessAmountMethodLabel,
  excessAmountMethodOptions,
  SubscriptionForIssueModel,
} from "./increaseOfShareCapitalDomain";
import { NumberFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { CURRENCY_NOK } from "@/utils/currency";
import { makeAutoObservable } from "mobx";
import { compact, identity, sortBy } from "lodash";
import Big from "big.js";
import { ShareType } from "@/common/share";
import { SharesPerShareType } from "@/dbot/common/commonDomain";
import { TMessageParam } from "@/i18n/tt";

export function calculateExcessAmount(
  subscribers: {
    debtContributionModel: DebtContributionModel;
  }[],
): number {
  return subscribers
    .map(
      (d) =>
        d.debtContributionModel.totalAmountReceivable.toNumberOrZero() -
        d.debtContributionModel.maxAmountToBeConverted.toNumberOrZero(),
    )
    .reduce((accum: number, n: number) => accum + n, 0);
}

export class DebtContributionModel {
  data?: DebtContribution;
  totalAmountReceivable: NumberFieldModel;
  maxAmountToBeConverted: NumberFieldModel;
  excessAmountMethod: RadioFieldModel<ExcessAmountMethod>;
  address: StringFieldModel;
  shareTypePriorityOrder: RadioFieldModel<ShareType[] | null>;

  constructor(
    readonly totalPriceForSubscriber: () => number,
    readonly subscriptions: () => SubscriptionForIssueModel[] | undefined,
    defaultExcessAmountMethod: ExcessAmountMethod,
    data?: DebtContribution,
    address?: string,
    params?: { required?: boolean },
  ) {
    this.data = data;
    this.totalAmountReceivable = new NumberFieldModel({
      value: data?.totalAmountReceivable,
      isPositiveOrZero: true,
      required: params?.required,
      onChange: (value) => {
        if (value !== undefined) {
          this.setExcessAmountMethodRequired(value > this.maxAmountToBeConverted.toNumberOrZero());
          this.maxAmountToBeConverted.validate();
        }
      },
    });
    this.maxAmountToBeConverted = new NumberFieldModel({
      value: data?.maxAmountToBeConverted,
      isPositiveOrZero: true,
      required: params?.required,
      validator: (value) => {
        if (value !== undefined && value > this.totalAmountReceivable.toNumberOrZero()) {
          return {
            key: "subscriber.debtConversion.amount.error.exceeding-total-amount-receivable",
            params: {
              value: {
                type: "amount",
                value: this.totalAmountReceivable.toNumberOrZero(),
                currency: CURRENCY_NOK,
              },
            },
          };
        } else if (value !== undefined && value > this.totalPriceForSubscriber()) {
          return {
            key: "subscriber.debtConversion.amount.error.exceeding-subscription-amount",
            params: {
              value: {
                type: "amount",
                value: this.totalPriceForSubscriber(),
                currency: CURRENCY_NOK,
              },
            },
          };
        }
        return undefined;
      },
      onChange: (value) => {
        if (value !== undefined) {
          this.setExcessAmountMethodRequired(this.totalAmountReceivable.toNumberOrZero() > value);
        }
      },
    });
    this.excessAmountMethod = new RadioFieldModel({
      value: data?.excessAmountMethod ?? defaultExcessAmountMethod,
      options: excessAmountMethodOptions,
      idFn: identity,
      labelFn: (v) => excessAmountMethodLabel[v],
      required: this.hasExcessAmount,
    });
    this.address = new StringFieldModel({ value: address, required: true });
    this.shareTypePriorityOrder = new RadioFieldModel<ShareType[] | null>({
      value: compact(
        data?.shareTypePriorityOrder?.map((sharetypeId) =>
          this.shareTypes.find((s) => s.sharetypeId === sharetypeId),
        ) ?? null,
      ),
      options: () => this.shareTypePriorityOrderOptions,
      idFn: (xs) => xs?.map((x) => x.sharetypeId).join("-") ?? "",
      labelFn: shareTypePriorityOrderLabel,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  onTotalPriceForSubscriberChange() {
    if (!this.maxAmountToBeConverted.isEmpty) {
      this.maxAmountToBeConverted.validate();
    }
  }

  get shareTypes() {
    const subscriptions = this.subscriptions();
    if (!subscriptions || subscriptions.length === 0) {
      return [];
    }
    return [...new Map(subscriptions.map((s) => [s.shareType.sharetypeId, s.shareType])).values()];
  }

  get showShareTypePriorityOrder() {
    const numberOfShareTypes = this.shareTypes.length;
    return numberOfShareTypes >= 2 && numberOfShareTypes <= 5;
  }

  get shareTypePriorityOrderOptions(): ShareType[][] {
    return this.shareTypes.length <= 5
      ? shareTypePriorityOrderPermutations(this.shareTypes)
      : [this.subscriptionsByShareTypePriorityOrder.map((s) => s.shareType)];
  }

  get subscriptionsByShareTypePriorityOrder(): SubscriptionForIssueModel[] {
    const priorityOf = ({ sharetypeId }: { sharetypeId: number }) => {
      const index =
        this.shareTypePriorityOrder.value?.findIndex((s) => s.sharetypeId === sharetypeId) ?? -1;
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    return sortBy(this.subscriptions(), priorityOf);
  }

  subscribedSharesForShareType(shareType: ShareType): number {
    return (
      this.subscriptions()
        ?.find((subscription) => subscription.shareType.sharetypeId === shareType.sharetypeId)
        ?.numberOfShares.toNumber() ?? 1
    );
  }

  get isEmpty(): boolean {
    return this.maxAmountToBeConverted.toNumberOrZero() === 0;
  }

  setExcessAmountMethodRequired(required: boolean) {
    this.excessAmountMethod.setRequired(required);
  }

  get hasExcessAmount(): boolean {
    return (
      this.totalAmountReceivable.toNumber() !== undefined &&
      this.maxAmountToBeConverted.toNumber() !== undefined &&
      this.totalAmountReceivable.toNumberOrZero() > this.maxAmountToBeConverted.toNumberOrZero()
    );
  }

  get excessAmount(): number {
    return this.hasExcessAmount
      ? this.totalAmountReceivable.toNumberOrZero() - this.maxAmountToBeConverted.toNumberOrZero()
      : 0;
  }

  get remaining(): number {
    return Math.max(
      0,
      this.totalPriceForSubscriber() - this.maxAmountToBeConverted.toNumberOrZero(),
    );
  }

  get isValid(): boolean {
    return (
      this.maxAmountToBeConverted.isValid &&
      this.totalAmountReceivable.isValid &&
      this.address.isValid &&
      this.excessAmountMethod.isValid
    );
  }

  validate(): boolean {
    this.maxAmountToBeConverted.validate();
    this.totalAmountReceivable.validate();
    this.address.validate();
    this.excessAmountMethod.validate();
    return this.isValid;
  }

  toDebtContribution(): DebtContribution | undefined {
    return {
      type: ContributionType.debt,
      maxAmountToBeConverted: this.maxAmountToBeConverted.toNumber(),
      totalAmountReceivable: this.totalAmountReceivable.toNumber(),
      excessAmountMethod: this.hasExcessAmount ? this.excessAmountMethod.value : undefined,
      shareTypePriorityOrder:
        this.shareTypePriorityOrder.value?.map((s) => s.sharetypeId) ??
        this.subscriptionsByShareTypePriorityOrder.map((s) => s.sharetypeId),
    };
  }
}

export function calculateNumberOfSharesWithNonCashContribution(
  nonCashContribution: number,
  subscriptions?: SubscriptionForIssueModel[],
): SharesPerShareType[] {
  if (subscriptions === undefined) {
    return [];
  }

  const totalContribution = subscriptions.reduce(
    (sum, s) =>
      sum.plus(
        Big(s.numberOfShares.toNumberOrZero()).times(s.subscriptionPrice.toNumberOrZero() ?? 0),
      ),
    Big(0),
  );

  if (totalContribution.gt(nonCashContribution)) {
    return calculateNumberOfSharesThroughNonCashContribution(subscriptions, nonCashContribution);
  }

  return subscriptions.map((s) => ({
    shareType: s.shareType,
    numberOfShares: s.numberOfShares.toNumberOrZero(),
  }));
}

export function calculateNumberOfSharesThroughNonCashContribution(
  subscriptions: SubscriptionForIssueModel[],
  totalContribution: number,
): SharesPerShareType[] {
  const { sharesPerType } = subscriptions
    .filter((s) => s.numberOfShares.toNumberOrZero() > 0)
    .reduce(
      (acc, subscription) => {
        if (acc.remaining.eq(0)) {
          return {
            ...acc,
            sharesPerType: [
              ...acc.sharesPerType,
              { shareType: subscription.shareType, numberOfShares: 0 },
            ],
          };
        }
        const subscriptionPrice = subscription.subscriptionPrice.toNumber() ?? 1;
        const numberOfSharesByShareType =
          calculateNumberOfSharesThroughRemainingNonCashContribution(
            acc.remaining,
            subscriptionPrice,
            subscription.numberOfShares.toNumberOrZero(),
          );
        const contributionByShareType = Big(numberOfSharesByShareType).times(subscriptionPrice);
        return {
          sharesPerType: [
            ...acc.sharesPerType,
            { shareType: subscription.shareType, numberOfShares: numberOfSharesByShareType },
          ],
          remaining: acc.remaining.minus(contributionByShareType),
        };
      },
      { sharesPerType: [] as SharesPerShareType[], remaining: Big(totalContribution) },
    );

  return sharesPerType;
}

export function calculateNumberOfSharesThroughRemainingNonCashContribution(
  remainingContribution: Big,
  subscriptionPrice: number,
  numberOfShares: number,
): number {
  const numberOfSharesThroughRemainingDebt = remainingContribution
    .div(subscriptionPrice)
    .round(0, Big.roundDown)
    .toNumber();
  return numberOfSharesThroughRemainingDebt > numberOfShares
    ? numberOfShares
    : numberOfSharesThroughRemainingDebt;
}

export function shareTypePriorityOrderPermutations(shareTypes: ShareType[]): ShareType[][] {
  if (shareTypes.length <= 1) return [shareTypes];
  return shareTypes.flatMap((item, i) => {
    const rest = [...shareTypes.slice(0, i), ...shareTypes.slice(i + 1)];
    return shareTypePriorityOrderPermutations(rest).map((perm) => [item, ...perm]);
  });
}

export function shareTypePriorityOrderLabel(option: ShareType[] | null): TMessageParam {
  return {
    type: "list",
    separator: " - ",
    value: option?.map((x) => ({ type: "shareType", value: x, options: { plain: true } })) ?? [],
  };
}
